import { Badge, Button, formatPrice } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { SALON_ID, api } from "../lib/trpc";

type Method = "card_terminal" | "cash" | "card_online" | "transfer" | "gift_card";

const METHODS: { key: Method; label: string; glyph: string }[] = [
  { key: "card_terminal", label: "Carte", glyph: "💳" },
  { key: "cash", label: "Espèces", glyph: "💶" },
  { key: "transfer", label: "Virement", glyph: "🏦" },
  { key: "gift_card", label: "Carte cadeau", glyph: "🎁" },
];

/** Pourboires proposés en un tap. Le champ libre reste possible. */
const TIP_SUGGESTIONS = [0, 200, 500, 1000];

/**
 * Encaissement.
 *
 * Deux règles de conception :
 *  1. Le RESTE À PAYER est déjà calculé par le serveur, acompte déduit. On ne
 *     demande jamais à une employée de faire une soustraction en caisse.
 *  2. Un seul écran, jamais un tunnel. Montant pré-rempli, moyen en un tap,
 *     validation. Une cliente qui attend son ticket ne doit pas voir trois
 *     étapes défiler.
 */
export function CheckoutPanel({
  appointmentId,
  onClose,
}: {
  appointmentId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<Method>("card_terminal");
  const [tipCents, setTipCents] = useState(0);
  const [amountInput, setAmountInput] = useState("");
  /**
   * Montant fige au moment de la validation.
   * Sans lui, le rechargement du recapitulatif remet le reste du a 0 et la
   * confirmation annonce « Encaisse 0 € » — l'employee ne sait plus si le
   * paiement est passe.
   */
  const [collected, setCollected] = useState<{ amount: number; tip: number } | null>(null);
  /** Code de carte cadeau saisi — ou repris de la réservation. */
  const [giftCode, setGiftCode] = useState("");
  /** Message après un prélèvement partiel sur carte : il reste à régler. */
  const [partialNotice, setPartialNotice] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ["checkout", appointmentId],
    queryFn: () => api.checkout.summary.query({ appointmentId }),
  });

  const data = summary.data;
  const announced = data?.giftCard && data.giftCard.state === "active" ? data.giftCard : null;

  /*
   * Carte vérifiée au fil de la frappe : dès que le code a la bonne longueur,
   * on affiche le solde. Personne n'a à cliquer sur « Vérifier » pour savoir
   * si la carte passe.
   */
  const normalizedCode = giftCode.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const lookup = useQuery({
    queryKey: ["gift-card-lookup", normalizedCode],
    queryFn: () => api.giftCards.lookup.query({ salonId: SALON_ID, code: normalizedCode }),
    enabled: method === "gift_card" && normalizedCode.length === 12,
    staleTime: 0,
  });
  const card = lookup.data;
  const cardUsable = card?.state === "active";

  // Montant pré-rempli avec le reste dû : le cas courant se valide sans saisie.
  // Sur carte cadeau, plafonné au solde : on ne propose jamais un refus.
  useEffect(() => {
    if (!data) return;
    const plafond = method === "gift_card" && cardUsable ? Math.min(data.remainingCents, card!.balanceCents) : data.remainingCents;
    setAmountInput((plafond / 100).toFixed(2));
  }, [data, method, cardUsable, card]);

  const chooseGiftCard = (code: string) => {
    setMethod("gift_card");
    setGiftCode(code);
    setTipCents(0);
  };

  const amountCents = useMemo(() => {
    const parsed = Number.parseFloat(amountInput.replace(",", "."));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
  }, [amountInput]);

  const isGift = method === "gift_card";
  const partialGift = isGift && data ? amountCents < data.remainingCents : false;

  const collect = useMutation({
    mutationFn: () =>
      api.checkout.collect.mutate({
        appointmentId,
        salonId: SALON_ID,
        amountCents,
        tipCents: isGift ? 0 : tipCents,
        method,
        giftCardCode: isGift ? normalizedCode : undefined,
        // Un prélèvement partiel sur carte laisse la prestation ouverte : le
        // reste se règle juste après, dans le même écran.
        complete: !partialGift,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["planning"] });
      queryClient.invalidateQueries({ queryKey: ["pulse"] });
      queryClient.invalidateQueries({ queryKey: ["checkout", appointmentId] });
      queryClient.invalidateQueries({ queryKey: ["gift-cards"] });
      queryClient.invalidateQueries({ queryKey: ["gift-card-lookup"] });

      if (partialGift && data) {
        setPartialNotice(
          `${formatPrice(amountCents)} prélevés sur la carte. Il reste ${formatPrice(data.remainingCents - amountCents)} à régler.`,
        );
        setMethod("card_terminal");
        setGiftCode("");
        collect.reset();
        return;
      }
      setCollected({ amount: amountCents, tip: isGift ? 0 : tipCents });
    },
  });

  const settled = collect.isSuccess;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Encaissement"
      className="fixed inset-0 z-50 flex items-center justify-center bg-encre-900/30 p-6 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-lift animate-fade-up"
        onClick={(event) => event.stopPropagation()}
      >
        {settled ? (
          <div className="py-6 text-center">
            <p className="mb-2 text-4xl">✅</p>
            <h2 className="text-2xl">Encaissé</h2>
            <p className="mt-1 text-encre-500">
              {formatPrice((collected?.amount ?? 0) + (collected?.tip ?? 0))}
              {collected && collected.tip > 0
                ? ` dont ${formatPrice(collected.tip)} de pourboire`
                : ""}
            </p>
            <Button className="mt-5" block onClick={onClose}>
              Terminer
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-2xl">Encaisser</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Fermer"
                className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
              >
                ×
              </button>
            </div>

            {summary.isPending && <p className="text-encre-400">Chargement…</p>}

            {data && (
              <>
                <ul className="mb-3 space-y-1 text-sm">
                  {data.appointment.lines.map((line) => (
                    <li key={line.id} className="flex justify-between gap-2">
                      <span className="text-encre-700">{line.nameSnapshot}</span>
                      <span className="tabular-nums text-encre-500">
                        {formatPrice(line.priceCentsSnapshot)}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* L'acompte déjà réglé est déduit ici, pas de tête. */}
                {data.alreadyPaidCents - data.giftCardPaidCents > 0 && (
                  <p className="mb-2 flex justify-between rounded-md bg-sauge-100 px-3 py-2 text-sm text-sauge-700">
                    <span>Déjà réglé</span>
                    <span className="tabular-nums">
                      − {formatPrice(data.alreadyPaidCents - data.giftCardPaidCents)}
                    </span>
                  </p>
                )}
                {data.giftCardPaidCents > 0 && (
                  <p className="mb-2 flex justify-between rounded-md bg-blush-100 px-3 py-2 text-sm text-blush-700">
                    <span>🎁 Réglé par carte cadeau</span>
                    <span className="tabular-nums">− {formatPrice(data.giftCardPaidCents)}</span>
                  </p>
                )}

                {partialNotice && (
                  <p role="status" className="mb-3 rounded-md bg-sauge-100 px-3 py-2 text-sm font-medium text-sauge-700">
                    ✅ {partialNotice}
                  </p>
                )}

                {/*
                  Carte annoncée à la réservation : proposée d'office. Sans ce
                  rappel, la cliente devait y penser elle-même au comptoir — et
                  celle qui l'oublie repart en ayant payé deux fois.
                */}
                {announced && !isGift && data.remainingCents > 0 && data.giftCardPaidCents === 0 && (
                  <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-blush-200 bg-blush-50 px-3 py-2.5 text-sm">
                    <span className="text-encre-700">
                      🎁 Carte cadeau annoncée · solde{" "}
                      <strong className="tabular-nums">{formatPrice(announced.balanceCents)}</strong>
                    </span>
                    <Button size="sm" onClick={() => chooseGiftCard(announced.code)}>
                      Utiliser
                    </Button>
                  </div>
                )}

                <p className="mb-4 flex items-baseline justify-between border-t border-creme-200 pt-3">
                  <span className="text-encre-600">Reste à payer</span>
                  <span className="font-display text-3xl text-encre-900" data-numeric>
                    {formatPrice(data.remainingCents)}
                  </span>
                </p>

                <fieldset className="mb-4">
                  <legend className="mb-2 text-sm font-medium text-encre-700">Moyen</legend>
                  <div className="grid grid-cols-2 gap-2">
                    {METHODS.map((entry) => (
                      <button
                        key={entry.key}
                        type="button"
                        onClick={() =>
                          entry.key === "gift_card" ? chooseGiftCard(announced?.code ?? giftCode) : setMethod(entry.key)
                        }
                        aria-pressed={method === entry.key}
                        className={[
                          "min-h-11 rounded-lg px-3 text-left text-sm transition-colors",
                          method === entry.key
                            ? "bg-blush-500 text-white"
                            : "bg-creme-100 text-encre-700 hover:bg-creme-200",
                        ].join(" ")}
                      >
                        <span aria-hidden className="mr-1.5">
                          {entry.glyph}
                        </span>
                        {entry.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                {isGift && (
                  <div className="mb-4">
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-medium text-encre-700">Code de la carte</span>
                      <input
                        value={giftCode}
                        onChange={(event) => setGiftCode(event.target.value)}
                        placeholder="XXXX-XXXX-XXXX"
                        autoFocus={!giftCode}
                        spellCheck={false}
                        autoCapitalize="characters"
                        className="h-12 w-full rounded-lg border border-creme-300 px-4 font-mono text-lg uppercase tracking-wider outline-none focus:border-blush-400 focus:ring-2 focus:ring-blush-200"
                      />
                    </label>
                    {normalizedCode.length > 0 && normalizedCode.length < 12 && (
                      <p className="mt-1 text-xs text-encre-400">12 caractères, tirets facultatifs.</p>
                    )}
                    {lookup.isFetching && <p className="mt-1 text-sm text-encre-400">Vérification…</p>}
                    {lookup.isSuccess && !card && (
                      <p role="alert" className="mt-1 text-sm font-medium text-grenat-700">
                        Aucune carte ne correspond à ce code.
                      </p>
                    )}
                    {card && (
                      <p
                        className={[
                          "mt-2 flex items-center justify-between rounded-md px-3 py-2 text-sm",
                          cardUsable ? "bg-sauge-100 text-sauge-700" : "bg-grenat-100 text-grenat-700",
                        ].join(" ")}
                      >
                        <span>
                          {card.stateLabel}
                          {card.recipientName ? ` · ${card.recipientName}` : ""}
                        </span>
                        {cardUsable && (
                          <span className="font-medium tabular-nums">Solde {formatPrice(card.balanceCents)}</span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {/* Pas de pourboire sur une carte cadeau : il se règle à part. */}
                {!isGift && (
                <fieldset className="mb-4">
                  <legend className="mb-2 text-sm font-medium text-encre-700">Pourboire</legend>
                  <div className="flex flex-wrap gap-2">
                    {TIP_SUGGESTIONS.map((cents) => (
                      <button
                        key={cents}
                        type="button"
                        onClick={() => setTipCents(cents)}
                        aria-pressed={tipCents === cents}
                        className={[
                          "min-h-9 rounded-full px-3.5 text-sm transition-colors",
                          tipCents === cents
                            ? "bg-encre-800 text-white"
                            : "bg-creme-100 text-encre-700 hover:bg-creme-200",
                        ].join(" ")}
                      >
                        {cents === 0 ? "Aucun" : formatPrice(cents)}
                      </button>
                    ))}
                  </div>
                </fieldset>
                )}

                <label className="mb-4 block">
                  <span className="mb-1.5 block text-sm font-medium text-encre-700">
                    Montant encaissé
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      inputMode="decimal"
                      value={amountInput}
                      onChange={(event) => setAmountInput(event.target.value)}
                      className="h-12 w-full rounded-lg border border-creme-300 px-4 text-lg tabular-nums outline-none focus:border-blush-400 focus:ring-2 focus:ring-blush-200"
                    />
                    <span className="text-lg text-encre-500">€</span>
                  </div>
                  {/* Paiement partiel : on le dit, on ne le bloque pas. */}
                  {amountCents > 0 && amountCents < data.remainingCents && (
                    <span className="mt-1 block text-xs text-ambre-700">
                      Paiement partiel — il restera{" "}
                      {formatPrice(data.remainingCents - amountCents)}
                      {isGift ? ", à régler juste après par un autre moyen." : "."}
                    </span>
                  )}
                  {isGift && cardUsable && amountCents > card!.balanceCents && (
                    <span role="alert" className="mt-1 block text-xs font-medium text-grenat-700">
                      Le solde de la carte est de {formatPrice(card!.balanceCents)}.
                    </span>
                  )}
                </label>

                {tipCents > 0 && (
                  <p className="mb-3 text-sm text-encre-500">
                    Total encaissé : <Badge tone="accent">{formatPrice(amountCents + tipCents)}</Badge>
                  </p>
                )}

                {collect.isError && (
                  <p role="alert" className="mb-3 text-sm font-medium text-grenat-700">
                    {collect.error instanceof Error && isGift
                      ? collect.error.message
                      : "L'encaissement n'a pas abouti. Réessayez."}
                  </p>
                )}

                <Button
                  block
                  size="lg"
                  loading={collect.isPending}
                  disabled={
                    amountCents <= 0 ||
                    (isGift && (!cardUsable || amountCents > (card?.balanceCents ?? 0)))
                  }
                  onClick={() => collect.mutate()}
                >
                  {isGift
                    ? `Prélever ${formatPrice(amountCents)} sur la carte`
                    : `Valider ${formatPrice(amountCents + tipCents)}`}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
